import React, { useState, useEffect } from 'react';
import { Paper, PrimaryButton } from 'library';
import { Grid, makeStyles, TextField } from '@material-ui/core';
import UserPostPicture from '../UserPostPicture';
import { track } from 'shared/Tracking';
import { useUserContext } from 'UserContext';
import theme from 'theme';
import client from 'shared/ApiClient';
import { consoleLogInDev } from 'shared/Utils';
import { CreationActionBar } from '../ActionBar';
import PendingAttachmentSection from '../Attachments/PendingAttachementSection';
import { getRandomPostPrompt } from '../Prompts';
import { addImages } from '../Utilities/addImageRequest';
import { useNewsfeedContext } from '../NewsfeedContext';
import IProvider from 'models/Providers/IProvider';

const useStyles = makeStyles({
    root: {
        flexGrow: 1,
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        padding: 0,
        margin: 0,
        '& .MuiOutlinedInput-root': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            borderRadius: '15px',
            flex: 1,
            flexDirection: 'column',
        },
    },
});

type AttachmentType = {
    type: string;
    data: { imageId?: number; providerId?: number };
};

export default function PostCreationContent({ refreshPosts }: { refreshPosts: () => void }) {
    const classes = useStyles();
    const { user } = useUserContext();
    const { groupId } = useNewsfeedContext();
    const [postText, setPostText] = useState<string>();
    const [prompt, setPrompt] = useState('');
    const [images, setImages] = useState([]);
    const [provider, setProvider] = useState<IProvider>();
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setPrompt(getRandomPostPrompt(!!groupId));
    }, []);

    async function savePost() {
        setIsSubmitting(true);
        try {
            let form = {
                content: postText,
                active: true,
                attachments: [] as AttachmentType[],
                group: groupId,
            };

            if (images.length > 0) {
                const imgs = await addImages(images, user);
                imgs.forEach((img) => {
                    form.attachments.push({
                        type: 'Image',
                        data: { imageId: img.id },
                    });
                });
            }

            if (provider) {
                form.attachments.push({
                    type: 'Provider',
                    data: { providerId: provider.id },
                });
            }

            track('Family: Post Created');
            await client('api/post/', {
                body: form,
            });
        } catch (e) {
            consoleLogInDev(e);
        } finally {
            setPostText('');
            setImages([]);
            refreshPosts();
            setIsSubmitting(false);
            setProvider(undefined);
        }
    }

    function handleSetImages(images: never[]) {
        setProvider(undefined);
        setImages(images as never[]);
    }

    function handleSetProvider(provider: IProvider) {
        setImages([]);
        setProvider(provider);
    }

    function handleChange(e: any) {
        setPostText(e.target.value);
    }

    return (
        <Paper style={{ marginBottom: 20 }}>
            <Grid container>
                <Grid item>
                    <UserPostPicture profileImage={user?.profile_picture ? user.profile_picture.image : ''} />
                </Grid>
                <Grid item style={{ flexGrow: 1 }}>
                    <Grid container>
                        <TextField
                            classes={classes}
                            onChange={handleChange}
                            multiline
                            variant="outlined"
                            placeholder={prompt}
                            value={postText}
                            InputProps={{
                                endAdornment: (
                                    <Grid container>
                                        <PendingAttachmentSection
                                            images={images}
                                            setImages={setImages}
                                            provider={provider}
                                            setProvider={setProvider}
                                        />
                                    </Grid>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid
                        container
                        style={{
                            paddingTop: 10,

                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Grid item>
                            <CreationActionBar
                                setImages={handleSetImages}
                                images={images}
                                setProvider={handleSetProvider}
                            />
                        </Grid>
                        <Grid item>
                            <PrimaryButton
                                disabled={(postText?.trim().length || 0) === 0}
                                onClick={() => !isSubmitting && savePost()}
                                loading={isSubmitting}
                            >
                                Post
                            </PrimaryButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
}
